import React from "react";
import { motion } from "framer-motion";
import { Copy, Plus, Trash2, Zap, X } from "lucide-react";
import { FICHES, ZONES, LABELS, ENERGIES_REMPLACEES, fmt, euros, pacList, ratioR } from "@/lib/cee";

const Label = ({ children }) => (
  <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">{children}</label>
);

const Choice = ({ idx, name, value, options, onChange, disabled }) => (
  <div className="flex flex-wrap gap-2">
    {Object.entries(options).map(([k, v]) => (
      <button
        type="button"
        key={k}
        data-testid={`op-${name}-${idx}-${k}`}
        disabled={disabled}
        onClick={() => onChange(k)}
        className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors disabled:cursor-not-allowed ${
          value === k ? "border-sky-500/60 bg-sky-500/10 text-[var(--accent-text)]" : "border-[var(--line)] bg-[var(--inset)] text-[var(--dim)] hover:border-[var(--line-hover)] disabled:opacity-40"
        }`}
      >
        {v}
      </button>
    ))}
  </div>
);

const NumField = ({ idx, name, label, value, onChange, placeholder, hint, step = "1" }) => (
  <div>
    <Label>{label}</Label>
    <input data-testid={`op-${name}-input-${idx}`} type="number" min="0" step={step} className="field-input"
      placeholder={placeholder} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    {hint && <p className="mt-1.5 text-[11px] text-[var(--mute)]">{hint}</p>}
  </div>
);

const Check = ({ idx, name, checked, onChange, children }) => (
  <label className="flex cursor-pointer items-start gap-2.5 text-[12px] text-[var(--dim)]">
    <input type="checkbox" data-testid={`op-${name}-${idx}`} checked={!!checked} onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 accent-[var(--brand)]" />
    <span>{children}</span>
  </label>
);

const CdpBox = ({ op, idx, set, children }) => (
  <div data-testid={`op-cdp-block-${idx}`} className="space-y-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4 sm:col-span-2">
    {children}
    {op.cdp && (
      <div>
        <Label>Énergie de la chaudière remplacée</Label>
        <Choice idx={idx} name="cdp-energie" value={op.energieRemplacee} options={ENERGIES_REMPLACEES} onChange={(v) => set({ energieRemplacee: v })} />
      </div>
    )}
  </div>
);

const Fields137 = ({ op, idx, set }) => (
  <>
    <NumField idx={idx} name="nb-logements" label="Nombre d'appartements raccordés" value={op.nbLogements} onChange={(v) => set({ nbLogements: v })}
      placeholder="Ex. 40" hint={op.cdp ? "Nombre total de logements raccordés à la même sous-station." : "Bâtiment résidentiel collectif uniquement."} />
    <CdpBox op={op} idx={idx} set={set}>
      <Check idx={idx} name="cdp-toggle" checked={op.cdp} onChange={(v) => set({ cdp: v })}>
        <b className="text-[var(--ink)]">Coup de pouce Chauffage collectif</b> — le raccordement à un réseau de chaleur efficace remplace une chaudière
        collective gaz ou fioul (opération engagée depuis le 01/01/2026). Montant bonifié par sous-station :
        N ≤ 125 logements : 24 000 × N + 9 000 000 kWh cumac · N &gt; 125 : 54 000 × N + 5 200 000 kWh cumac.
      </Check>
    </CdpBox>
  </>
);

const Reseau179 = ({ reseau, idx }) => {
  const known = reseau && reseau.isEligible !== undefined;
  return (
    <div data-testid={`op-reseau-check-${idx}`} className={`rounded-xl border p-3 text-[12px] sm:col-span-2 ${
      !known ? "border-[var(--line)] text-[var(--mute)]" : reseau.isEligible ? "border-amber-500/40 bg-amber-500/5 text-[var(--amber-text)]" : "border-emerald-500/30 bg-emerald-500/5 text-[var(--green-text)]"}`}>
      <b>Raccordement à un réseau de chaleur : </b>
      {!known
        ? "choisissez l'adresse des travaux dans la liste pour vérifier la proximité d'un réseau (France Chaleur Urbaine)."
        : reseau.isEligible
          ? `réseau « ${reseau.name || "réseau"} » à ${reseau.distance ?? "?"} m — un raccordement est envisageable ; pour le Coup de pouce, l'impossibilité de raccordement doit être justifiée par le gestionnaire du réseau.`
          : "aucun réseau à proximité de l'adresse — raccordement non envisageable (source France Chaleur Urbaine)."}
    </div>
  );
};

/* BAR-TH-179 : une puissance par PAC, total PAC / chaufferie → R */
const Pacs179 = ({ op, idx, set }) => {
  const pacs = Array.isArray(op.pacs) && op.pacs.length ? op.pacs : pacList(op).map((p) => (p ? String(p) : ""));
  const setPac = (i, v) => set({ pacs: pacs.map((p, j) => (j === i ? v : p)) });
  const total = pacList({ pacs });
  const sum = total.reduce((a, b) => a + b, 0);
  const chauf = parseFloat(op.puissanceChaufferie) || 0;
  return (
    <div data-testid={`op-pacs-${idx}`} className="space-y-2 sm:col-span-2">
      <Label>Puissances thermiques nominales des PAC (Prated à -10 °C, ≤ 400 kW chacune)</Label>
      {pacs.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-16 shrink-0 font-mono text-xs text-[var(--dim)]">PAC {i + 1}</span>
          <input data-testid={`op-pac-input-${idx}-${i}`} type="number" min="0" step="0.1" className="field-input" placeholder="Ex. 120"
            value={p} onChange={(e) => setPac(i, e.target.value)} />
          <span className="text-xs text-[var(--mute)]">kW</span>
          <button type="button" data-testid={`op-pac-remove-${idx}-${i}`} disabled={pacs.length <= 1} title="Retirer cette PAC"
            onClick={() => set({ pacs: pacs.filter((_, j) => j !== i) })}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:text-[var(--rose-text)] disabled:opacity-30">
            <X size={13} />
          </button>
        </div>
      ))}
      <button type="button" data-testid={`op-pac-add-${idx}`} onClick={() => set({ pacs: [...pacs, ""] })} className="btn-ghost w-full text-xs">
        <Plus size={13} /> Ajouter une PAC
      </button>
      <NumField idx={idx} name="puissance-chaufferie" label="Puissance totale chaufferie après travaux (kW)" value={op.puissanceChaufferie} step="0.1"
        onChange={(v) => set({ puissanceChaufferie: v })} placeholder="Ex. 250" hint="Toutes PAC incluses, hors secours." />
      <div data-testid={`op-pac-recap-${idx}`} className="grid grid-cols-3 gap-2 rounded-xl border border-[var(--line)] bg-[var(--inset)] p-3 text-center">
        {[["Puissance totale PAC", `${fmt(sum, 1)} kW`, "total"], ["Puissance totale chaufferie", chauf ? `${fmt(chauf, 1)} kW` : "—", "chaufferie"],
          ["R", fmt(Math.round(ratioR(sum, chauf) * 1000) / 1000, 3), "r"]].map(([l, v, k]) => (
          <div key={k}>
            <div data-testid={`op-pac-${k}-${idx}`} className="font-mono text-sm font-bold text-[var(--ink)]">{v}</div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--mute)]">{l}</div>
          </div>
        ))}
        <p className="col-span-3 text-[10px] text-[var(--mute)]">R = PAC totale / chaufferie si &lt; 0,4 ; sinon R = 1.</p>
      </div>
    </div>
  );
};

const Fields179 = ({ op, idx, set, reseau }) => (
  <>
    <Reseau179 reseau={reseau} idx={idx} />
    <div>
      <Label>Usage de la PAC</Label>
      <Choice idx={idx} name="usage" value={op.usage} options={LABELS.usage}
        onChange={(v) => set(v === "chauffage_ecs" ? { usage: v, application: "moyenne_haute" } : { usage: v })} />
    </div>
    <div>
      <Label>Type d'application</Label>
      <Choice idx={idx} name="application" value={op.application}
        options={op.usage === "chauffage_ecs" ? { moyenne_haute: LABELS.application.moyenne_haute } : LABELS.application}
        onChange={(v) => set({ application: v })} />
    </div>
    <NumField idx={idx} name="etas" label="Efficacité saisonnière Etas (%)" value={op.etas} onChange={(v) => set({ etas: v })}
      placeholder="Ex. 140" hint="Minimum : 111 % (moyenne / haute T°) ou 126 % (basse T°)." />
    <NumField idx={idx} name="nb-logements" label="Nombre d'appartements chauffés" value={op.nbLogements} onChange={(v) => set({ nbLogements: v })} placeholder="Ex. 30" />
    <Pacs179 op={op} idx={idx} set={set} />
    <CdpBox op={op} idx={idx} set={set}>
      <Check idx={idx} name="cdp-toggle" checked={op.cdp} onChange={(v) => set({ cdp: v })}>
        <b className="text-[var(--ink)]">Coup de pouce Chauffage collectif</b> — la PAC remplace une chaudière gaz ou fioul
        (opération engagée depuis le 01/01/2026). Volume de la fiche ×3.
      </Check>
      {op.cdp && (
        <Check idx={idx} name="cdp-raccordement" checked={op.cdpRaccordementImpossible} onChange={(v) => set({ cdpRaccordementImpossible: v })}>
          Impossibilité technique ou économique du raccordement à un réseau de chaleur justifiée (attestation du gestionnaire de réseau,
          ou absence de réseau dans la commune et les communes limitrophes).
        </Check>
      )}
    </CdpBox>
  </>
);

const Fields127 = ({ op, idx, set }) => (
  <>
    <div>
      <Label>Système hygroréglable</Label>
      <Choice idx={idx} name="type-systeme" value={op.typeSysteme} options={{ A: "Type A", B: "Type B" }} onChange={(v) => set({ typeSysteme: v })} />
    </div>
    <div>
      <Label>Caisson</Label>
      <select data-testid={`op-caisson-select-${idx}`} className="field-input" value={op.caisson} onChange={(e) => set({ caisson: e.target.value })}>
        {Object.entries(LABELS.caisson).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </div>
    <NumField idx={idx} name="nb-logements" label="Nombre de logements ventilés" value={op.nbLogements} onChange={(v) => set({ nbLogements: v })}
      placeholder="Ex. 24" hint="Installation collective uniquement." />
  </>
);

const FIELDS = { "BAR-TH-137": Fields137, "BAR-TH-179": Fields179, "BAR-TH-127": Fields127 };

export const OperationCard = ({ op, idx, total, result, reseau, showSplit, zoneLocked, onChange, onDuplicate, onDelete }) => {
  const Fields = FIELDS[op.fiche];
  return (
    <motion.div layout initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="card-surface p-5" data-testid={`operation-card-${idx}`}>
      <div className="mb-4 flex items-center justify-between">
        <span className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--accent-text)]">
          <Zap size={13} /> Opération {idx + 1}
        </span>
        <div className="flex gap-2">
          <button type="button" data-testid={`op-duplicate-btn-${idx}`} onClick={onDuplicate} title="Dupliquer"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] transition hover:border-sky-500/40 hover:text-[var(--accent-text)]">
            <Copy size={13} />
          </button>
          <button type="button" data-testid={`op-delete-btn-${idx}`} onClick={onDelete} disabled={total <= 1}
            title={total <= 1 ? "Le dossier doit comporter au moins une opération." : "Supprimer"}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] transition hover:border-rose-500/40 hover:text-[var(--rose-text)] disabled:opacity-30">
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        {Object.entries(FICHES).map(([ref, f]) => (
          <button type="button" key={ref} data-testid={`op-fiche-toggle-${idx}-${ref}`} onClick={() => onChange({ fiche: ref })}
            className={`rounded-xl border p-3 text-left transition-all ${op.fiche === ref ? "border-sky-500/60 bg-sky-500/10" : "border-[var(--line)] bg-[var(--inset)] hover:border-[var(--line-hover)]"}`}>
            <span className={`font-mono text-xs font-bold ${op.fiche === ref ? "text-[var(--accent-text)]" : "text-[var(--dim)]"}`}>
              {ref} <span data-testid={`op-fiche-version-${idx}-${ref}`} className="font-normal opacity-70">{f.version}</span>
            </span>
            <span className="mt-0.5 block text-[11px] leading-snug text-[var(--mute)]">{f.shortLabel}</span>
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label>Zone climatique</Label>
          <Choice idx={idx} name="zone" value={op.zone} options={Object.fromEntries(ZONES.map((z) => [z, z]))} onChange={(v) => onChange({ zone: v })} disabled={zoneLocked} />
          {zoneLocked && (
            <p data-testid={`op-zone-locked-${idx}`} className="mt-1.5 text-[11px] text-[var(--mute)]">Zone déterminée par l'adresse des travaux (non modifiable).</p>
          )}
        </div>
        {Fields && <Fields op={op} idx={idx} set={onChange} reseau={reseau} />}
      </div>

      {result.issues.length > 0 && (
        <p data-testid={`op-issues-${idx}`} className="mt-3 text-[11px] text-[var(--amber-text)]">{result.issues.join(" · ")}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-[var(--line-soft)] bg-[var(--inset)] px-4 py-3 text-xs">
        <span className="text-[var(--mute)]">Calcul : <b className="text-[var(--ink)]">{result.kwhCumac ? result.detail : "—"}</b></span>
        <span className="text-[var(--mute)]">kWh cumac : <b data-testid={`op-kwh-${idx}`} className="text-[var(--ink)]">{result.kwhCumac ? fmt(Math.round(result.kwhCumac)) : "—"}</b></span>
        {showSplit && <span className="text-[var(--mute)]">Classique / précarité : <b data-testid={`op-split-${idx}`} className="text-[var(--ink)]">{result.kwhCumac ? `${fmt(Math.round(result.kwhClassique))} / ${fmt(Math.round(result.kwhPrecarite))} kWh` : "—"}</b></span>}
        <span className="ml-auto text-[var(--mute)]">Prime : <b data-testid={`op-prime-${idx}`} className="text-[var(--accent-text)]">{result.prime == null ? "—" : euros(result.prime)}</b></span>
      </div>
    </motion.div>
  );
};
